"use client";

import { useActionState, useState, useEffect, useRef, useSyncExternalStore } from "react";
import type { ComponentType } from "react";
import { useFormStatus } from "react-dom";
import { registerStoreOwner } from "@/actions/register";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PasswordStrength } from "./password-strength";
import { AuthSocialButtons } from "./auth-social-buttons";
import {
    AlertCircle, ArrowRight, Loader2, Store, User, Mail, Lock, Phone, Eye, EyeOff, Check, X,
} from "lucide-react";
import Link from "next/link";

const noopSubscribe = () => () => {};

const inputCls =
    "flex-1 min-w-0 bg-transparent outline-none text-[16px] sm:text-[15px] text-foreground placeholder:text-muted-foreground/60";

/** Wrapper de campo padrão DS: label + caixa (ícone-líder + conteúdo) + erro de campo. */
function FieldShell({
    id, label, optional, icon: Icon, error, children,
}: {
    id: string;
    label: string;
    optional?: boolean;
    icon: ComponentType<{ className?: string }>;
    error?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="flex flex-col gap-[7px]">
            <label htmlFor={id} className="flex items-baseline gap-1.5 text-[13px] font-semibold text-foreground">
                {label}
                {optional && <span className="text-[12px] font-normal text-muted-foreground/70">(opcional)</span>}
            </label>
            <div
                data-error={error ? "true" : undefined}
                className="group flex h-[46px] items-center gap-2.5 rounded-[8px] border border-border bg-input px-3.5 transition-colors hover:border-accent/40 focus-within:border-accent focus-within:bg-card focus-within:ring-2 focus-within:ring-accent/25 data-[error=true]:border-destructive data-[error=true]:bg-[var(--mp-neg-bg)]"
            >
                <Icon className="h-[15px] w-[15px] shrink-0 text-muted-foreground/70 transition-colors group-focus-within:text-accent" />
                {children}
            </div>
            {error && <p className="text-[12px] text-destructive">{error}</p>}
        </div>
    );
}

function EyeToggle({ shown, onToggle }: { shown: boolean; onToggle: () => void }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-label={shown ? "Ocultar senha" : "Mostrar senha"}
            className="flex items-center rounded-[5px] p-0.5 text-muted-foreground/70 transition-colors hover:text-accent"
        >
            {shown ? <EyeOff className="h-[16px] w-[16px]" /> : <Eye className="h-[16px] w-[16px]" />}
        </button>
    );
}

function SubmitButton({ isDisabled }: { isDisabled: boolean }) {
    const { pending } = useFormStatus();
    return (
        <Button
            type="submit"
            disabled={pending || isDisabled}
            className="mt-2 h-12 w-full rounded-[8px] bg-accent text-[15px] font-bold text-accent-foreground transition-colors hover:bg-[#5742c4] active:translate-y-px disabled:opacity-50"
        >
            {pending ? (
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Criando conta...
                </>
            ) : (
                <>
                    Criar minha loja
                    <ArrowRight className="ml-2 h-4 w-4" />
                </>
            )}
        </Button>
    );
}

export function RegisterForm() {
    const [state, dispatch] = useActionState(registerStoreOwner, null);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isPasswordValid, setIsPasswordValid] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showPw, setShowPw] = useState(false);
    const [showPw2, setShowPw2] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);

    // Indicação de afiliado: lê ?ref= (window.location evita Suspense do useSearchParams);
    // o cookie httpOnly do middleware é a fonte primária, o hidden input é redundância.
    const refCode = useSyncExternalStore(
        noopSubscribe,
        () => {
            const ref = new URLSearchParams(window.location.search).get("ref");
            return ref && /^[A-Z0-9]{4,12}$/i.test(ref) ? ref.toUpperCase() : "";
        },
        () => ""
    );

    useEffect(() => {
        if (!refCode) return;
        const dedupeKey = `affiliate_click_${refCode}`;
        if (!sessionStorage.getItem(dedupeKey)) {
            sessionStorage.setItem(dedupeKey, "1");
            fetch("/api/public/affiliate-click", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code: refCode }),
            }).catch(() => { /* métrica best-effort */ });
        }
    }, [refCode]);

    // Erro do servidor → shake no card + foco no 1º campo inválido.
    useEffect(() => {
        if (!state || state.success || !state.message) return;
        const el = formRef.current;
        if (!el) return;
        el.classList.remove("mp-shake");
        void el.offsetWidth; // reinicia a animação
        el.classList.add("mp-shake");
        const firstInvalid = el.querySelector<HTMLInputElement>('[data-error="true"] input');
        firstInvalid?.focus();
    }, [state]);

    const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
    const isFormValid = isPasswordValid && passwordsMatch && acceptedTerms;

    if (state?.success) {
        return (
            <div className="flex flex-col items-center justify-center space-y-4 py-8 text-center">
                <div className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--mp-pos-bg)] text-[var(--mp-pos-text)]">
                    <Store className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-bold text-foreground">Loja criada com sucesso!</h3>
                <p className="max-w-xs text-muted-foreground">
                    Sua conta foi criada. Você já pode acessar o painel administrativo.
                </p>
                <Link href="/login" className="w-full">
                    <Button className="mt-4 h-11 w-full rounded-[8px] bg-accent text-accent-foreground hover:bg-[#5742c4]">
                        Ir para Login
                    </Button>
                </Link>
            </div>
        );
    }

    return (
        <form ref={formRef} action={dispatch} className="flex flex-col gap-4 text-left">
            {refCode && <input type="hidden" name="ref" value={refCode} />}

            {/* Cadastro social */}
            <AuthSocialButtons />

            {/* Divisor */}
            <div className="relative flex items-center gap-3 py-1">
                <span className="h-px flex-1 bg-border" />
                <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.11em] text-muted-foreground/60">
                    Ou cadastre com email
                </span>
                <span className="h-px flex-1 bg-border" />
            </div>

            <FieldShell id="storeName" label="Nome da Loja" icon={Store} error={state?.errors?.storeName?.[0]}>
                <input id="storeName" name="storeName" className={inputCls} placeholder="Minha Loja Inc." autoComplete="organization" required />
            </FieldShell>

            <FieldShell id="name" label="Seu Nome Completo" icon={User} error={state?.errors?.name?.[0]}>
                <input id="name" name="name" className={inputCls} placeholder="João Silva" autoComplete="name" required />
            </FieldShell>

            <FieldShell id="email" label="Email Corporativo" icon={Mail} error={state?.errors?.email?.[0]}>
                <input id="email" name="email" type="email" className={inputCls} placeholder="contato@minhaloja.com" autoComplete="email" required />
            </FieldShell>

            <FieldShell id="phone" label="Telefone / WhatsApp" optional icon={Phone}>
                <input id="phone" name="phone" type="tel" className={inputCls} placeholder="+55 11 99999-9999" autoComplete="tel" />
            </FieldShell>

            {/* Senha + checklist */}
            <div className="flex flex-col gap-2">
                <FieldShell id="password" label="Senha Forte" icon={Lock} error={state?.errors?.password?.[0]}>
                    <input
                        id="password"
                        name="password"
                        type={showPw ? "text" : "password"}
                        className={inputCls}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <EyeToggle shown={showPw} onToggle={() => setShowPw((v) => !v)} />
                </FieldShell>
                <PasswordStrength password={password} onValidityChange={setIsPasswordValid} />
            </div>

            {/* Confirmar senha + hint de coincidência */}
            <div className="flex flex-col gap-2">
                <FieldShell id="confirmPassword" label="Confirmar Senha" icon={Lock} error={state?.errors?.confirmPassword?.[0]}>
                    <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showPw2 ? "text" : "password"}
                        className={inputCls}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <EyeToggle shown={showPw2} onToggle={() => setShowPw2((v) => !v)} />
                </FieldShell>
                {confirmPassword.length > 0 && (
                    <p className={`flex items-center gap-1.5 text-[11.5px] ${passwordsMatch ? "text-[var(--mp-pos-text)]" : "text-destructive"}`}>
                        {passwordsMatch ? <Check className="h-3 w-3" strokeWidth={2.5} /> : <X className="h-3 w-3" strokeWidth={2.5} />}
                        {passwordsMatch ? "As senhas coincidem" : "As senhas não coincidem"}
                    </p>
                )}
            </div>

            {/* Termos (gate do CTA) */}
            <label className="flex cursor-pointer select-none items-start gap-2.5">
                <Checkbox
                    id="terms"
                    checked={acceptedTerms}
                    onCheckedChange={(v) => setAcceptedTerms(v === true)}
                    className="mt-0.5"
                />
                <span className="text-[12.5px] leading-relaxed text-muted-foreground">
                    Concordo com os <Link href="/terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-accent hover:underline">Termos de Serviço</Link> e a{" "}
                    <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-accent hover:underline">Política de Privacidade</Link>.
                </span>
            </label>

            {/* Erro global do servidor */}
            {state?.message && !state.success && (
                <div className="flex items-center gap-2 rounded-[8px] border border-destructive/20 bg-destructive/10 p-3 text-[13px] text-destructive">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{state.message}</span>
                </div>
            )}

            <SubmitButton isDisabled={!isFormValid} />

            <p className="mt-1 text-center text-[13.5px] text-muted-foreground">
                Já tem uma conta? <Link href="/login" className="font-semibold text-accent hover:underline">Fazer Login</Link>
            </p>
        </form>
    );
}
